// api.js â€” Dark Pattern Review Tool
// Centralised API service layer. All backend communication goes through here.
// Auth token is stored in localStorage under key: `cr_token`

const BASE = () => process.env.REACT_APP_API_BASE_URL || "http://localhost:8080";

// ── Token helpers ─────────────────────────────────────────────────────────────
const TOKEN_KEY = "cr_token";
const REFRESH_TOKEN_KEY = "cr_refresh_token";
const USER_KEY = "cr_user";
const USER_ID_KEY = "cr_userId";
const ORG_ID_KEY = "cr_orgId";

export const saveTokens = (accessToken, refreshToken) => {
    localStorage.setItem(TOKEN_KEY, accessToken);
    if (refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
};
export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const getRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_KEY);

export const clearToken = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(USER_ID_KEY);
    localStorage.removeItem(ORG_ID_KEY);
};

export const saveUser = (user, orgId) => {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    if (user?.user_id) localStorage.setItem(USER_ID_KEY, user.user_id);
    if (orgId) localStorage.setItem(ORG_ID_KEY, orgId);
};

export const getUser = () => {
    try { return JSON.parse(localStorage.getItem(USER_KEY)); } catch { return null; }
};
export const getUserId = () => localStorage.getItem(USER_ID_KEY);
export const getOrgId = () => localStorage.getItem(ORG_ID_KEY);
export const isLoggedIn = () => !!getToken();

const forceLogout = () => {
    clearToken();
    if (typeof window !== "undefined") {
        window.location.href = "/login";
    }
};

const isTokenExpired = (token) => {
    if (!token) return true;
    try {
        const parts = token.split(".");
        if (parts.length < 2) return true;
        const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
        if (!payload?.exp) return false;
        const now = Math.floor(Date.now() / 1000);
        return payload.exp <= now;
    } catch {
        return true;
    }
};

const ensureValidAccessToken = async () => {
    const token = getToken();
    if (!token) throw new Error("Not authenticated");
    if (!isTokenExpired(token)) return token;
    try {
        const refreshed = await refreshAccessToken();
        if (!refreshed?.access_token) throw new Error("Session expired. Please log in again.");
        return refreshed.access_token;
    } catch (e) {
        forceLogout();
        throw new Error("Session expired. Please log in again.");
    }
};

// ── Base fetch wrapper ────────────────────────────────────────────────────────
const request = async (method, path, body = null, requiresAuth = true) => {
    const headers = { "Content-Type": "application/json" };
    if (requiresAuth) {
        const token = await ensureValidAccessToken();
        headers["Authorization"] = `Bearer ${token}`;
    }

    const options = { method, headers };
    if (body) options.body = JSON.stringify(body);

    const baseUrl = BASE();
    const res = await fetch(`${baseUrl}${path}`, options);

    // Handle 401 — attempt refresh or logout
    if (res.status === 401 && requiresAuth) {
        try {
            const newTokens = await refreshAccessToken();
            if (newTokens?.access_token) {
                // Retry original request
                headers["Authorization"] = `Bearer ${newTokens.access_token}`;
                const retryRes = await fetch(`${baseUrl}${path}`, { ...options, headers });
                return await handleResponse(retryRes);
            }
        } catch (e) {
            forceLogout();
            throw new Error("Session expired. Please log in again.");
        }
    }

    return await handleResponse(res);
};

const handleResponse = async (res) => {
    let data;
    try { data = await res.json(); } catch { data = {}; }
    if (!res.ok) {
        throw new Error(data.message || data.error || data.detail || `Request failed (${res.status})`);
    }
    return data;
};

const buildAuthHeaders = (token, includeJson = true) => {
    const headers = {};
    if (includeJson) headers["Content-Type"] = "application/json";
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return headers;
};

const fetchWithAuthRetry = async (path, options = {}) => {
    const baseUrl = BASE();
    const token = await ensureValidAccessToken();
    const initialHeaders = { ...(options.headers || {}), ...buildAuthHeaders(token, false) };
    const firstRes = await fetch(`${baseUrl}${path}`, { ...options, headers: initialHeaders });

    if (firstRes.status === 401) {
        const refreshed = await refreshAccessToken();
        if (!refreshed?.access_token) throw new Error("Session expired. Please log in again.");
        const retryHeaders = { ...(options.headers || {}), ...buildAuthHeaders(refreshed.access_token, false) };
        return await fetch(`${baseUrl}${path}`, { ...options, headers: retryHeaders });
    }

    return firstRes;
};

// ── Auth (/auth) ─────────────────────────────────────────────────────────────
const REVIEW_TOOL_ID = "8dbe2f1d-f9e4-4f83-94f9-ce4440b6a656";

export const login = async (email, password) => {
    const data = await request("POST", "/auth/login", { email, password, tool_id: REVIEW_TOOL_ID }, false);
    if (data.access_token) {
        saveTokens(data.access_token, data.refresh_token);
        // Fallback to the submitted email if the backend doesn't return it
        const userEmail = data.email || email;
        saveUser({ user_id: data.user_id, email: userEmail }, data.org_id);
    }
    return data;
};

export const loginSSO = async (email) => {
    return request("POST", "/auth/login-sso", { email, tool_id: REVIEW_TOOL_ID }, false);
};

export const refreshAccessToken = async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) throw new Error("No refresh token");
    const data = await request("POST", "/auth/refresh", { refresh_token: refreshToken, tool_id: REVIEW_TOOL_ID }, false);
    if (data.access_token) {
        localStorage.setItem(TOKEN_KEY, data.access_token);
    }
    return data;
};

export const logout = async () => {
    const refreshToken = getRefreshToken();
    if (refreshToken) {
        try { await request("POST", "/auth/logout", { refresh_token: refreshToken }); } catch { /* ignore */ }
    }
    clearToken();
};

export const resetPassword = async (email, newPassword) => {
    return request("POST", "/auth/reset-password", { email, new_password: newPassword }, false);
};

// ── Review Tool (/review/v1) ──────────────────────────────────────────────────

export const fetchSessions = async () => {
    const res = await request("GET", "/review/v1/sessions");
    return res.sessions || [];
};

export const fetchSession = async (sessionId) => {
    const res = await request("GET", `/review/v1/sessions/${sessionId}`);
    return res.data; // Expecting { session, user_journeys }
};

export const fetchJourneyNavigation = async (journeyId) => {
    const res = await request("GET", `/review/v1/journeys/${journeyId}/navigation`);
    return res.data; // Expecting { journey_id, user_journey_name, navigationData: [...] }
};

export const updateImage = async (imageId, payload) => {
    return request("PATCH", `/review/v1/images/${imageId}`, payload);
};

export const addDarkPattern = async (payload) => {
    return request("POST", "/review/v1/dark-patterns", payload);
};

export const updateDarkPattern = async (id, payload) => {
    return request("PATCH", `/review/v1/dark-patterns/${id}`, payload);
};

export const confirmDarkPattern = async (id) => {
    return request("POST", `/review/v1/dark-patterns/${id}/confirm`);
};

export const revokeDarkPattern = async (id) => {
    return request("POST", `/review/v1/dark-patterns/${id}/revoke`);
};

export const addLaw = async (payload) => {
    // Spec: POST /review/v1/dark-patterns/{id}/laws
    const { dark_pattern_id, ...lawData } = payload;
    return request("POST", `/review/v1/dark-patterns/${dark_pattern_id}/laws`, lawData);
};

export const updateLaw = async (id, payload) => {
    return request("PATCH", `/review/v1/laws/${id}`, payload);
};

export const fetchImageProcessingData = async (imageId) => {
    const res = await request("GET", `/review/v1/images/${imageId}/processing-data`);
    return res.data;
};

export const fetchAgentTraces = async (imageId) => {
    const res = await request("GET", `/review/v1/agent-traces/${imageId}`);
    return res.data;
};

export const fetchLawMaster = async () => {
    const res = await request("GET", "/review/v1/laws/master");
    return res?.data?.rows || res?.rows || [];
};

export const saveImageFeedback = async (imageId, feedback) => {
    return request("POST", `/review/v1/images/${imageId}/feedback`, { feedback });
};

export const exportImageProperties = async (imageId, payload) => {
    const res = await fetchWithAuthRetry(`/review/v1/images/${imageId}/export-properties`, {
        method: "POST",
        headers: buildAuthHeaders(null, true),
        body: JSON.stringify(payload)
    });
    if (!res.ok) {
        let data; try { data = await res.json(); } catch { data = {}; }
        throw new Error(data.message || data.error || `Export failed (${res.status})`);
    }
    return await res.blob();
};

export const exportSessionReport = async (sessionId, payload) => {
    const res = await fetchWithAuthRetry(`/review/v1/sessions/${sessionId}/export-report`, {
        method: "POST",
        headers: buildAuthHeaders(null, true),
        body: JSON.stringify(payload)
    });
    if (!res.ok) {
        let data; try { data = await res.json(); } catch { data = {}; }
        throw new Error(data.message || data.error || `Export failed (${res.status})`);
    }
    return await res.blob();
};

/**
 * Stream a high-level session summary from the LLM.
 * Calls onToken(text) for each streamed token, then onDone() when complete,
 * or onError(message) on failure.
 */
export const generateSessionSummary = async (sessionId, { onToken, onDone, onError }) => {
    const token = await ensureValidAccessToken();
    const baseUrl = BASE();
    const res = await fetch(`${baseUrl}/review/v1/sessions/${sessionId}/generate-summary`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
    });

    if (!res.ok) {
        let data; try { data = await res.json(); } catch { data = {}; }
        throw new Error(data.detail || data.message || `Request failed (${res.status})`);
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop(); // keep incomplete last line
        for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            try {
                const payload = JSON.parse(line.slice(6));
                if (payload.status === "token" && onToken) onToken(payload.text);
                else if (payload.status === "done" && onDone) onDone();
                else if (payload.status === "error" && onError) onError(payload.message);
            } catch { /* ignore malformed line */ }
        }
    }
};

/** Export the (possibly edited) summary_text as a branded PDF blob. */
export const exportSessionSummary = async (sessionId, payload) => {
    const res = await fetchWithAuthRetry(`/review/v1/sessions/${sessionId}/export-summary`, {
        method: "POST",
        headers: buildAuthHeaders(null, true),
        body: JSON.stringify(payload),
    });
    if (!res.ok) {
        let data; try { data = await res.json(); } catch { data = {}; }
        throw new Error(data.detail || data.message || `Export failed (${res.status})`);
    }
    return await res.blob();
};



// â”€â”€ YT Scans (Videos tab) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

export const fetchYtScans = async () => {
    const res = await request("GET", "/review/v1/yt/scans");
    return res.scans || [];
};

export const fetchYtScan = async (scanId) => {
    const res = await request("GET", `/review/v1/yt/scans/${scanId}`);
    return res.scan;
};

export const updateYtPattern = async (scanId, patternId, payload) => {
    return request("PATCH", `/review/v1/yt/scans/${scanId}/patterns/${patternId}`, payload);
};

export const updateYtFeedback = async (scanId, internal_feedback) => {
    return request("PATCH", `/review/v1/yt/scans/${scanId}/feedback`, { internal_feedback });
};

export const deleteYtPattern = async (scanId, patternId) => {
    return request("DELETE", `/review/v1/yt/scans/${scanId}/patterns/${patternId}`);
};
export const addYtPattern = async (scanId) => {
    return request("POST", `/review/v1/yt/scans/${scanId}/patterns`, {});
};